import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Layout, Flex, Typography, Button, message, Drawer } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import MessageHeader from './MessageHeader';
import MessageContent from './MessageContent';
import MessageDetailsDrawer from './MessageDetailsDrawer';
import MessageFooter from './MessageFooter/MessageFooter';
import MessageInputReplyPreview from './MessageInputReplyPreview/MessageInputReplyPreview';
import TicketTableView from '../../Tickets/TicketTableView/TicketTableView';
import TicketDetail from '../../Tickets/TicketDetail/TicketDetail';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import useMessageFetching from '../hooks/useMessageFetching';
import useContactStatus from '../hooks/useContactStatus';
import useDraftAttachments from '../hooks/useDraftAttachments';
import useMessageSeenTracking from '../hooks/useMessageSeenTracking';
import useSelectedMessageSync from '../hooks/useSelectedMessageSync';
import useMessageScrollManagement from '../hooks/useMessageScrollManagement';
import useMessageSocketEvents from '../hooks/useMessageSocketEvents';
import useContactSocketSync from '../hooks/useContactSocketSync';
import { sendMessage, deleteMessage } from '../MessagesServices';

const getFileUploadSignature = (conversation) => (
  (conversation?.file_uploads || [])
    .map(file => `${file?.id || ''}:${file?.updated_at || file?.file_url || ''}`)
    .join('|')
);

const getContactSignature = (conversation) => {
  const contact = conversation?.contact;
  if (!contact) return '';

  return [
    contact.id,
    contact.first_name,
    contact.last_name,
    contact.picture_url,
    contact.contact_type,
    contact.is_ignored,
    contact.is_snoozed,
    contact.email,
    contact.phone,
    contact.locale,
    contact.timezone,
  ].map(value => value ?? '').join('|');
};

const Messages = React.memo(({ selectedConversation, onBack, setConversations, setSelectedConversation, screens }) => {
  const { isDarkMode } = useTheme();
  const { handleError } = useErrorHandler();

  // ===== Local UI State =====
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);

  // ===== Tickets Sidebar State =====
  const [showTickets, setShowTickets] = useState(false);
  const [ticketsWidth, setTicketsWidth] = useState(350);
  const [isResizingTickets, setIsResizingTickets] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const containerRef = useRef(null);

  const handleToggleTickets = useCallback(() => {
    setShowTickets(prev => !prev);
  }, []);

  const handleDataLoaded = useCallback((total) => {
    // Sidebar will remain open and display the empty state from TicketTableView
  }, []);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizingTickets(true);
  }, []);

  useEffect(() => {
    if (!isResizingTickets) return;

    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = containerRect.right - e.clientX;
      const maxWidth = containerRect.width - 400; // Min messages width
      setTicketsWidth(Math.max(250, Math.min(newWidth, maxWidth))); // Min tickets width
    };

    const handleMouseUp = () => {
      setIsResizingTickets(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingTickets]);

  const ticketFilters = useMemo(() => {
    return selectedConversation?.contact?.id ? { contact_id: selectedConversation.contact.id } : {};
  }, [selectedConversation?.contact?.id]);

  // ===== Custom Hooks =====
  const { draftAttachments, clearDrafts } = useDraftAttachments(selectedConversation);
  const { markMessageAsSeen } = useMessageSeenTracking(selectedConversation, setConversations);
  const { footerFocusRef, listScrollApiRef, scrollToBottom, focusFooterInput } = useMessageScrollManagement();

  const {
    messages,
    loading,
    hasMore,
    hasMoreNewer,
    messageRefs,
    handleScroll,
    addMessage,
    updateMessage,
    deleteMessage: removeMessage,
    isInitialLoading,
    isFetchingMoreTop,
    isFetchingMoreBottom,
    fetchMessagesAroundMessage,
    fetchLatestMessages
  } = useMessageFetching(selectedConversation, handleError, markMessageAsSeen);

  const {
    isIgnored,
    isSnoozed,
    isAdmin,
    updateContactStatus
  } = useContactStatus(selectedConversation, setConversations, setSelectedConversation);

  // ===== Socket Synchronization =====
  useSelectedMessageSync(messages, selectedMessage, setSelectedMessage, drawerVisible, setDrawerVisible);
  useMessageSocketEvents(selectedConversation, messages, addMessage, updateMessage, removeMessage, markMessageAsSeen);
  useContactSocketSync(selectedConversation, setSelectedConversation, setConversations);

  // Reset reply preview when switching conversations
  useEffect(() => {
    setReplyTo(null);
  }, [selectedConversation?.id]);

  // ===== Event Handlers =====
  const handleReplyClick = useCallback((replyTo) => {
    if (!replyTo || !replyTo.id) {
      return;
    }

    const targetMessageId = replyTo.id;
    const targetMessage = messages.find(m => m.id === targetMessageId);

    if (targetMessage && messageRefs.current[targetMessageId]?.current) {
      // Message is already loaded, scroll to it
      messageRefs.current[targetMessageId].current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      setHighlightedMessageId(targetMessageId);
      setTimeout(() => setHighlightedMessageId(null), 2000);
    } else if (targetMessageId) {
      // Message found in memory but not loaded, fetch messages around it
      fetchMessagesAroundMessage(targetMessageId).then(() => {
        // Wait for render, then scroll to the message
        setTimeout(() => {
          if (messageRefs.current[targetMessageId]?.current) {
            messageRefs.current[targetMessageId].current.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
            setHighlightedMessageId(targetMessageId);
            setTimeout(() => setHighlightedMessageId(null), 2000);
          }
        }, 200);
      }).catch((error) => {
        handleError(error);
      });
    }
  }, [messages, messageRefs, fetchMessagesAroundMessage, handleError]);


  const handleReplyAction = useCallback((messageItem) => {
    setReplyTo(messageItem);
    focusFooterInput();
  }, [focusFooterInput]);

  const handleJumpToLatest = useCallback(async () => {
    // The initial message page is already the latest page. Reload only when
    // this view was opened around an older message and newer pages remain.
    if (hasMoreNewer) {
      await fetchLatestMessages();
    }
    scrollToBottom();
  }, [hasMoreNewer, fetchLatestMessages, scrollToBottom]);

  const handleSendMessage = useCallback(async (messageData) => {
    scrollToBottom();
    setIsSending(true);

    try {
      const payload = {
        attachment_ids: messageData.attachment_ids || [],
        reply_to_message_id: replyTo?.id || undefined
      };

      if (messageData.message && messageData.message.trim()) {
        payload.content = messageData.message;
      }

      await sendMessage(selectedConversation?.id, payload);

      clearDrafts();
      setReplyTo(null);
      scrollToBottom();
    } catch (error) {
      handleError(error);
    } finally {
      setIsSending(false);
    }
  }, [selectedConversation?.id, handleError, replyTo?.id, scrollToBottom, clearDrafts]);

  const handleDeleteMessage = useCallback(async (messageId) => {
    await deleteMessage(messageId);
  }, []);

  // ===== Styles =====
  const layoutStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    width: '100%',
    background: isDarkMode ? '#141414' : '#fff',
    overflow: 'hidden'
  };

  // ===== Render =====
  return (
    <Flex
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        userSelect: isResizingTickets ? 'none' : undefined,
        cursor: isResizingTickets ? 'col-resize' : undefined,
      }}
    >
      <Layout style={{ ...layoutStyle, flex: 1 }}>
        {/* Header */}
        <MessageHeader
          screens={screens}
          onBack={onBack}
          isIgnored={isIgnored}
          isSnoozed={isSnoozed}
          isAdmin={isAdmin}
          updateContactStatus={updateContactStatus}
          selectedConversation={selectedConversation}
          onToggleTickets={handleToggleTickets}
          showTickets={showTickets}
        />

      {/* Message List */}
      <MessageContent
        screens={screens}
        selectedConversation={selectedConversation}
        messages={messages}
        loading={loading}
        isInitialLoading={isInitialLoading}
        isFetchingMoreTop={isFetchingMoreTop}
        isFetchingMoreBottom={isFetchingMoreBottom}
        hasMore={hasMore}
        hasMoreNewer={hasMoreNewer}
        messageRefs={messageRefs}
        handleScroll={handleScroll}
        handleReplyClick={handleReplyClick}
        onReplyAction={handleReplyAction}
        setSelectedMessage={setSelectedMessage}
        setDrawerVisible={setDrawerVisible}
        onDeleteMessage={handleDeleteMessage}
        scrollApiRef={listScrollApiRef}
        onJumpToLatest={handleJumpToLatest}
        highlightedMessageId={highlightedMessageId}
      />

      {/* Message Details Drawer */}
      <MessageDetailsDrawer
        screens={screens}
        drawerVisible={drawerVisible}
        selectedMessage={selectedMessage}
        setDrawerVisible={setDrawerVisible}
        setSelectedMessage={setSelectedMessage}
        onDeleteMessage={handleDeleteMessage}
      />

      {/* Reply Preview */}
      <MessageInputReplyPreview
        replyTo={replyTo}
        onCancel={() => setReplyTo(null)}
      />

      {/* Footer Input */}
      <MessageFooter
        onSendMessage={handleSendMessage}
        isSending={isSending}
        focusInputRef={footerFocusRef}
        conversationId={selectedConversation?.id}
        initialFileList={draftAttachments}
      />
    </Layout>

      {/* Tickets Resizable Sidebar (Desktop) */}
      {screens.lg && showTickets && (
        <>
          <div
            className={`ticket-detail-divider${isDarkMode ? ' dark' : ''}${isResizingTickets ? ' active' : ''}`}
            onMouseDown={handleResizeStart}
          />
          <div
            style={{
              width: ticketsWidth,
              flex: `0 0 ${ticketsWidth}px`,
              background: isDarkMode ? '#141414' : '#fff',
              borderLeft: `1px solid ${isDarkMode ? '#434343' : '#f0f0f0'}`,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Flex align="center" justify="space-between" style={{ padding: '12px 16px', borderBottom: `1px solid ${isDarkMode ? '#434343' : '#f0f0f0'}` }}>
              <Typography.Text strong>Tickets</Typography.Text>
              <Button type="text" icon={<CloseOutlined />} onClick={handleToggleTickets} size="small" />
            </Flex>
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <TicketTableView
                filters={ticketFilters}
                forceCompact={true}
                onDataLoaded={handleDataLoaded}
                onTicketClick={(ticket) => setSelectedTicketId(ticket.id)}
              />
            </div>
          </div>
        </>
      )}

      {/* Tickets Drawer (Tablet/Mobile) */}
      {!screens.lg && (
        <Drawer
          title="Tickets"
          placement="right"
          onClose={handleToggleTickets}
          open={showTickets}
          width={screens.xs ? '100%' : 400}
          styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' } }}
        >
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <TicketTableView
              filters={ticketFilters}
              forceCompact={true}
              onDataLoaded={handleDataLoaded}
              onTicketClick={(ticket) => setSelectedTicketId(ticket.id)}
            />
          </div>
        </Drawer>
      )}

      {/* Ticket Detail Modal */}
      {selectedTicketId && (
        <TicketDetail
          ticketId={selectedTicketId}
          onClose={() => setSelectedTicketId(null)}
        />
      )}
    </Flex>
  );
}, (prevProps, nextProps) => (
  prevProps.selectedConversation?.id === nextProps.selectedConversation?.id &&
  prevProps.selectedConversation?.is_writing === nextProps.selectedConversation?.is_writing &&
  prevProps.selectedConversation?.processing_delay_applied_at === nextProps.selectedConversation?.processing_delay_applied_at &&
  prevProps.selectedConversation?.processing_delay_applied_seconds === nextProps.selectedConversation?.processing_delay_applied_seconds &&
  getContactSignature(prevProps.selectedConversation) === getContactSignature(nextProps.selectedConversation) &&
  getFileUploadSignature(prevProps.selectedConversation) === getFileUploadSignature(nextProps.selectedConversation) &&
  prevProps.onBack === nextProps.onBack &&
  prevProps.setConversations === nextProps.setConversations &&
  prevProps.setSelectedConversation === nextProps.setSelectedConversation &&
  prevProps.screens?.xs === nextProps.screens?.xs &&
  prevProps.screens?.sm === nextProps.screens?.sm &&
  prevProps.screens?.md === nextProps.screens?.md &&
  prevProps.screens?.lg === nextProps.screens?.lg
));

export default Messages;
